import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Checkbox from '@material-ui/core/Checkbox'
import Divider from '@material-ui/core/Divider'
import FormControl from '@material-ui/core/FormControl'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import FormLabel from '@material-ui/core/FormLabel'
import Grid from '@material-ui/core/Grid'
import Radio from '@material-ui/core/Radio'
import RadioGroup from '@material-ui/core/RadioGroup'
import TextField from '@material-ui/core/TextField'
import { makeStyles } from '@material-ui/core/styles'
import SaveAltIcon from '@material-ui/icons/SaveAlt'
import Autocomplete from '@material-ui/lab/Autocomplete'
import moment from 'moment'
import React, { useCallback, useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import GridTextField from '../../components/GridTextField'
import HeaderDetails from '../../components/HeaderDetails'
import { StoreState } from '../../store'
import { saveTransaction } from '../../store/transactions/transactionsActions'
import { TransactionsActionTypes } from '../../store/transactions/transactionsTypes'
import { applyMaskOnChange, setMask } from '../../utils'

const useStyles = makeStyles((theme) => ({
  container: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  input: {
    margin: theme.spacing(1),
  },
  marginRight: {
    marginRight: theme.spacing(1),
  },
}))

interface Props {
  people: any[]
  categories: any[]
}

interface Form {
  isMember: boolean
  type: string
  value: string
  parcel: string
  date: string
  categoryId: string
  status: string
  historic: string
  provider: string
  resource: string
  costCenter: string
  fiscalNote: string
  source: string
  paymentMethod: string
  personId: string
  personName: string
}

const emptyForm: Form = {
  isMember: true,
  type: 'Receita',
  value: '',
  parcel: '',
  date: moment().format('YYYY-MM-DD'),
  categoryId: '',
  status: 'Pago',
  historic: '',
  provider: '',
  resource: 'Caixa',
  costCenter: '',
  fiscalNote: '',
  source: '',
  paymentMethod: '',
  personId: '',
  personName: '',
}

export default function TransactionsDetails({ people, categories }: Props) {
  const classes = useStyles()
  const dispatch = useDispatch()
  const { savingTransaction, isEditing, originalData } = useSelector(
    ({ transactionsState }: StoreState) => ({
      savingTransaction: transactionsState.savingTransaction,
      isEditing: transactionsState.selectedIndex !== null,
      originalData:
        transactionsState.selectedIndex !== null
          ? transactionsState.transactions[transactionsState.selectedIndex]
          : null,
    })
  )

  const form = useForm<Form>({
    defaultValues: !originalData
      ? emptyForm
      : {
          isMember: !!originalData.personId,
          type: originalData.value > 0 ? 'Receita' : 'Despesa',
          value: setMask('real', originalData.value.toFixed(2)),
          parcel: originalData.parcel,
          date: originalData.date,
          categoryId: originalData.categoryId,
          status: originalData.status,
          historic: originalData.historic || '',
          provider: originalData.provider || '',
          resource: originalData.resource || 'Caixa',
          costCenter: originalData.costCenter || '',
          fiscalNote: originalData.fiscalNote || '',
          source: originalData.source || '',
          paymentMethod: originalData.paymentMethod || '',
          personId: originalData.personId || '',
          personName: originalData.personName || '',
        },
  })

  useEffect(() => {
    form.register({ name: 'isMember' })
    form.register({ name: 'type' })
    form.register({ name: 'status' })
    form.register({ name: 'categoryId' })
    form.register({ name: 'personId' })
    form.register({ name: 'resource' })
    form.register({ name: 'source' })
    form.register({ name: 'paymentMethod' })
    form.register({ name: 'costCenter' })
  }, [form])

  const isMember = form.watch('isMember')
  const type = form.watch('type')
  const status = form.watch('status')
  const categoryId = form.watch('categoryId')
  const personId = form.watch('personId')
  const resource = form.watch('resource')
  const source = form.watch('source')
  const paymentMethod = form.watch('paymentMethod')
  const costCenter = form.watch('costCenter')

  const onClickSave = useCallback(async () => {
    await form.triggerValidation()
    const data = form.getValues()
    if (!categoryId) {
      form.setError('categoryId', '', 'Escolha a categoria')
    }
    if (!categoryId) {
      form.setError('categoryId', '', 'Escolha a categoria')
    }
    if (
      !parseFloat(
        data.value.replace('R$ ', '').replace('.', '').replace(',', '.')
      )
    ) {
      form.setError('value', '', 'Preencha um valor')
    }
    if (!moment(data.date, 'YYYY-MM-DD').isValid()) {
      form.setError('date', '', 'Preencha uma data válida')
    }
    if (Object.entries(form.errors).length) {
      return
    }
    dispatch(saveTransaction(data))
    form.reset(emptyForm)
  }, [dispatch, form, categoryId])

  const categoriesOptions = useMemo(() => {
    const currentType = type === 'Receita' ? 'incomes' : 'expenses'
    const category = categories
      .filter((item) => item.active && item.type === currentType)
      .map((item) => ({ name: item.name, value: item.id }))

    return category
  }, [categories, type])

  return (
    <Box className={classes.container}>
      <HeaderDetails
        title={isEditing ? 'Editando cadastro' : 'Novo cadastro'}
        onClose={() =>
          dispatch({ type: TransactionsActionTypes.EDITING_CANCEL })
        }
      />
      <Box margin={2} height="100%">
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} md={6}>
            <FormControl component="fieldset" disabled={isEditing}>
              <FormLabel component="legend">Tipo</FormLabel>
              <RadioGroup
                row
                name="type"
                value={type}
                onChange={(e: any) => form.setValue('type', e.target.value)}
              >
                <FormControlLabel
                  value="Receita"
                  control={<Radio color="primary" />}
                  label="Receita"
                  labelPlacement="end"
                />
                <FormControlLabel
                  value="Despesa"
                  control={<Radio color="primary" />}
                  label="Despesa"
                  labelPlacement="end"
                />
              </RadioGroup>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={12} md={6}>
            <FormControl component="fieldset">
              <FormLabel component="legend">Status</FormLabel>
              <RadioGroup
                row
                name="status"
                value={status}
                onChange={(e: any) => form.setValue('status', e.target.value)}
              >
                <FormControlLabel
                  value="Pago"
                  control={<Radio color="primary" />}
                  label="Pago"
                  labelPlacement="end"
                />
                <FormControlLabel
                  value="Pendente"
                  control={<Radio color="primary" />}
                  label="Pendente"
                  labelPlacement="end"
                />
                <FormControlLabel
                  value="Cancelado"
                  control={<Radio color="primary" />}
                  label="Cancelado"
                  labelPlacement="end"
                />
              </RadioGroup>
            </FormControl>
          </Grid>

          <GridTextField
            label="Valor *"
            name="value"
            placeholder="R$ 0,00"
            gridSize={3}
            inputRef={form.register}
            errorMessage={form.errors?.value?.message}
            onChange={applyMaskOnChange('real')}
          />
          <GridTextField
            label="Parcela"
            name="parcel"
            type="number"
            gridSize={3}
            inputRef={form.register}
          />
          <GridTextField
            label="Data de vencimento *"
            name="date"
            type="date"
            gridSize={3}
            inputRef={form.register}
            errorMessage={form.errors?.date?.message}
          />
          <GridTextField
            label="Categoria"
            options={categoriesOptions}
            gridSize={3}
            value={categoryId}
            onChange={(e: any) => form.setValue('categoryId', e.target.value)}
            errorMessage={form.errors?.categoryId?.message}
          />
          <GridTextField
            label="Histórico"
            name="historic"
            gridSize={12}
            inputRef={form.register}
          />

          {type === 'Receita' ? (
            <>
              {!isMember ? (
                <GridTextField
                  name="personName"
                  label="Nome da pessoa"
                  inputRef={form.register}
                  gridSize={8}
                />
              ) : (
                <Grid item md={8} xs={12} sm={12}>
                  {!!people.length && (
                    <Autocomplete
                      value={
                        personId
                          ? people.find((item: any) => item.id === personId)
                          : null
                      }
                      options={people}
                      getOptionLabel={(option: any) => option?.name || ''}
                      getOptionSelected={(option, value) =>
                        option.id === value.id
                      }
                      onChange={(_, selected: any) =>
                        form.setValue('personId', selected?.id || '')
                      }
                      noOptionsText="Nenhum cadastro encontrado"
                      filterOptions={(options, { inputValue }) => {
                        return options.filter((option) => {
                          return option.name
                            .toLowerCase()
                            .includes(inputValue.toLowerCase())
                        })
                      }}
                      renderInput={(params) => (
                        <TextField {...params} label="Membro" fullWidth />
                      )}
                    />
                  )}
                </Grid>
              )}
              <Grid item md={4} xs={12} sm={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isMember}
                      onChange={() => form.setValue('isMember', !isMember)}
                    />
                  }
                  label="Membro"
                  style={{ marginTop: 16 }}
                />
              </Grid>
            </>
          ) : (
            <GridTextField
              label="Fornecedor"
              name="provider"
              gridSize={12}
              inputRef={form.register}
            />
          )}

          {type === 'Receita' && (
            <>
              <GridTextField
                label="Recurso"
                gridSize={4}
                options={['Caixa', 'Conta', 'Outros']}
                value={resource}
                onChange={(e: any) => form.setValue('resource', e.target.value)}
              />
              <GridTextField
                label="Origem"
                gridSize={4}
                options={['Bazar', 'Cantina', 'Culto', 'Ensino', 'Evento']}
                value={source}
                onChange={(e: any) => form.setValue('source', e.target.value)}
              />
              <GridTextField
                label="Forma de pagamento"
                gridSize={4}
                options={[
                  'Dinheiro',
                  'Cartão de crédito',
                  'Cartão de débito',
                  'Cheque',
                  'Boleto',
                  'Pix',
                ]}
                value={paymentMethod}
                onChange={(e: any) =>
                  form.setValue('paymentMethod', e.target.value)
                }
              />
            </>
          )}
          {type === 'Despesa' && (
            <>
              <GridTextField
                label="Recurso"
                gridSize={6}
                options={['Caixa', 'Conta', 'Outros']}
                value={resource}
                onChange={(e: any) => form.setValue('resource', e.target.value)}
              />
              <GridTextField
                label="Centro de custo"
                gridSize={6}
                options={['Administração', 'Batismo', 'Células', 'Cultos']}
                value={costCenter}
                onChange={(e: any) =>
                  form.setValue('costCenter', e.target.value)
                }
              />
            </>
          )}

          {type === 'Despesa' && (
            <GridTextField
              label="Nota Fiscal"
              name="fiscalNote"
              gridSize={12}
              inputRef={form.register}
            />
          )}
        </Grid>
      </Box>
      <Box>
        <Divider />
        <Box m={2}>
          <Button
            onClick={onClickSave}
            size="large"
            color="primary"
            variant="contained"
            disabled={savingTransaction}
            className={classes.marginRight}
          >
            <SaveAltIcon className={classes.marginRight} />
            {savingTransaction ? 'AGUARDE...' : 'SALVAR'}
          </Button>
        </Box>
      </Box>
    </Box>
  )
}
